import { inject, Injectable, signal } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApiService } from '../api-services/base-api/api.service';
import { getTokenExpiration } from '../utils/jwt.util';
import { PlatformService } from './platform.service';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { DotNetRoleClaim } from '../authorization/permissions';
import { SKIP_AUTH_REFRESH } from '../interceptors/auth-context';

interface ApiEnvelope<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private accessTokenKey = 'access_token';
  private refreshTokenKey = 'refresh_token';
  private refreshTimeout: any;
  private readonly api = inject(ApiService);
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);

  /** Reactive signal that components can read for current auth state */
  readonly isLoggedInSignal = signal(false);
  /** The source of truth for JWT-derived state. Updated on every token replacement. */
  readonly accessTokenSignal = signal<string | null>(null);
  /** Reactive signal for the current user object (or null) */
  readonly currentUserSignal = signal<any>(null);

  constructor() {
    // Initialise signals from stored session on service creation
    this.refreshSignals();
    const storedToken = this.getAccessToken();
    if (storedToken) this.scheduleRefresh(storedToken);
  }

  /** Re-read localStorage and update signals */
  private refreshSignals(): void {
    const accessToken = this.getAccessToken();
    this.accessTokenSignal.set(accessToken);
    this.isLoggedInSignal.set(!!accessToken);
    this.currentUserSignal.set(this.readCurrentUser());
  }

  setSession(accessToken: string, refreshToken: string) {
    if (!this.platform.isBrowser) return;
    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    this.scheduleRefresh(accessToken);
    this.refreshSignals();
  }

  getAccessToken(): string | null {
    if (!this.platform.isBrowser) {
      return null;
    }
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    if (!this.platform.isBrowser) {
      return null;
    }
    return localStorage.getItem(this.refreshTokenKey);
  }

  clearSession() {
    if (!this.platform.isBrowser) return;
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem('current_user');
    clearTimeout(this.refreshTimeout);
    this.refreshSignals();
  }

  isLoggedIn(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return false;
    }

    try {
      if (getTokenExpiration(accessToken) <= Date.now()) {
        this.clearSession();
        return false;
      }
    } catch {
      this.clearSession();
      return false;
    }

    return true;
  }

  private scheduleRefresh(token: string) {
    let expiresAt: number;
    try {
      expiresAt = getTokenExpiration(token);
    } catch {
      this.signOut();
      return;
    }
    const now = Date.now();
    const refreshTime = expiresAt - now - 60000;

    if (refreshTime <= 0) {
      this.refreshToken();
      return;
    }

    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(() => {
      this.refreshToken();
    }, refreshTime);
  }

  completeExternalLogin(data: { accessToken: string; refreshToken: string; user: any }): void {
    this.setSession(data.accessToken, data.refreshToken);
    if (data.user) {
      localStorage.setItem('current_user', JSON.stringify(data.user));
    }
    this.refreshSignals();
  }

  login(credentials: { userName: string; password: string }): import('rxjs').Observable<any> {
    return this.api.post('authentication/login', credentials).pipe(
      tap((response: any) => {
        const data = response.data || response;
        if (data.accessToken && data.refreshToken) {
          this.setSession(data.accessToken, data.refreshToken);
          if (data.user) {
            localStorage.setItem('current_user', JSON.stringify(data.user));
          }
          this.refreshSignals();
        }
      })
    );
  }

  register(data: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    identityTypeId: number;
    identityNumber: string;
    institutionId: number;
    courseId: number;
    userName: string;
    password: string;
    address: string;
  }): import('rxjs').Observable<any> {
    return this.api.post('authentication/register', data);
  }

  getCurrentUser(): any {
    return this.currentUserSignal();
  }

  /** Returns the role issued in the access token, falling back to the login response. */
  getUserRole(): string {
    const accessToken = this.getAccessToken();
    if (accessToken) {
      try {
        const payload = jwtDecode<Record<string, string | string[]>>(accessToken);
        const role = this.normaliseClaim(payload[DotNetRoleClaim] ?? payload['role'])[0];
        if (role) {
          return role;
        }
      } catch {
        // The session validation path handles malformed tokens.
      }
    }

    const storedRole = this.currentUserSignal()?.role;
    return typeof storedRole === 'string' ? storedRole : '';
  }

  /** Direct localStorage read (used internally for initialisation) */
  private readCurrentUser(): any {
    try {
      const token = this.getAccessToken();
      if (!token) return null;
      const payload = jwtDecode<Record<string, string | string[]>>(token);
      const roles = this.normaliseClaim(payload[DotNetRoleClaim] ?? payload['role']);
      const stored = localStorage.getItem('current_user');
      const storedUser = stored ? JSON.parse(stored) : {};
      return {
        ...storedUser,
        id: payload['sub'] || payload['nameid'],
        firstName: payload['given_name'] || payload['firstName'] || storedUser.firstName || '',
        lastName: payload['family_name'] || payload['lastName'] || storedUser.lastName || '',
        email: payload['email'] || storedUser.email || '',
        emailAddress: payload['email'] || storedUser.emailAddress || '',
        // Keep this legacy field for current views; authorization uses all JWT roles.
        role: roles[0] || storedUser.role || '',
        emailConfirmed: payload['emailConfirmed'] || payload['email_verified'] || false,
        phoneNumberConfirmed: payload['phoneNumberConfirmed'] || payload['phone_number_verified'] || false,
      };
    } catch {
      return null;
    }
  }

  /** Refreshes the access token and rebuilds all JWT-derived state. */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token is available.'));
    }

    return this.api.post<ApiEnvelope<TokenResponse>>(
      'authentication/refresh',
      { refreshToken },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) },
    ).pipe(
      map((response) => response.data ?? (response as unknown as TokenResponse)),
      tap((response) => this.setSession(response.accessToken, response.refreshToken)),
      map((response) => response.accessToken),
    );
  }

  refreshToken(): void {
    this.refreshAccessToken().pipe(
      catchError((error) => {
        this.signOut();
        return throwError(() => error);
      }),
    ).subscribe({ error: () => undefined });
  }

  signOut(returnUrl?: string): void {
    this.clearSession();
    if (this.platform.isBrowser) {
      void this.router.navigate(['/auth/login'], {
        queryParams: returnUrl ? { returnUrl } : undefined,
      });
    }
  }

  private normaliseClaim(value: string | string[] | undefined): string[] {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return values.filter((item): item is string => typeof item === 'string' && !!item.trim());
  }
}
